/**
 * Utilities for the LLM-driven apply-block system.
 *
 * The LLM returns apply markers in its response text:
 *   <<<APPLY blockId="abc" action="replace" label="Purpose">>>
 *   ## Purpose
 *   Updated content here...
 *   <<<END_APPLY>>>
 *
 * This module provides:
 * - parseApplyBlocks: extract apply markers from text
 * - extractBlockOutline: build block outline from TipTap JSON for context
 */

// ── Types ──

export interface ApplyBlock {
  blockId: string
  action: 'replace' | 'insert-after' | 'delete'
  label: string
  content: string
  /** Raw matched string (for splitting the text around it) */
  raw: string
}

export interface ParsedContent {
  /** Segments of text and apply blocks in order */
  segments: Array<
    | { type: 'text'; content: string }
    | { type: 'apply'; block: ApplyBlock }
    | { type: 'malformed'; raw: string }
  >
}

export interface BlockOutlineEntry {
  id: string
  type: string
  level?: number
  text: string
}

// ── Regex ──

// Match an apply-block, absorbing any surrounding ``` code fences the LLM
// may wrap it in (its training example shows the format inside a fence).
// Without absorbing the fences, they become orphan text segments that
// render as empty code blocks in the chat.
const APPLY_BLOCK_RE =
  /(?:```[a-zA-Z0-9_-]*[ \t]*\n)?<<<APPLY\s+((?:blockId|after)="[^"]*"\s+)?action="(replace|insert-after|delete)"\s+label="([^"]*)">>>\n([\s\S]*?)<<<END_APPLY>>>(?:[ \t]*\n?```)?/g

// Detect a malformed apply-block: anything that ends in <<<END_APPLY>>>
// preceded by something that looks like an opener attempt but does NOT
// satisfy APPLY_BLOCK_RE. Captures the broken segment so the UI can
// replace it with a "couldn't render edit" notice instead of leaking raw
// markers like `<<>> ... <<<END_APPLY>>>` into the rendered chat.
//
// Heuristic: scan back from each <<<END_APPLY>>> to the nearest line that
// could plausibly be an opener (contains << or >> and is on its own line
// or at the start of the captured chunk). We stay deliberately greedy on
// the opener side and lazy on the closer side so a single malformed block
// is captured as one segment rather than fragmenting nearby prose.
const MALFORMED_APPLY_RE = /(?:^|\n)([ \t]*<{1,3}[^\n]*?\n[\s\S]*?<<<END_APPLY>>>)(?:[ \t]*\n?```)?/g

// ── Parsing ──

/**
 * Parses assistant text into segments: plain text and apply blocks.
 * If no apply markers are found, returns a single text segment.
 */
export function parseApplyBlocks(text: string): ParsedContent {
  // First pass: collect all VALID apply-block matches with their ranges.
  type ValidMatch = { start: number; end: number; block: ApplyBlock }
  const validMatches: ValidMatch[] = []
  for (const match of text.matchAll(APPLY_BLOCK_RE)) {
    const idAttr = match[1]?.trim() ?? ''
    const action = match[2] as ApplyBlock['action']
    const label = match[3]
    const content = match[4].trim()
    let blockId = ''
    const blockIdMatch = idAttr.match(/(?:blockId|after)="([^"]*)"/)
    if (blockIdMatch) blockId = blockIdMatch[1]

    validMatches.push({
      start: match.index!,
      end: match.index! + match[0].length,
      block: { blockId, action, label, content, raw: match[0] },
    })
  }

  // Second pass: find malformed end-tags whose ranges DON'T overlap a valid
  // match. We mark those for a "could not render edit" inline notice.
  type MalformedMatch = { start: number; end: number; raw: string }
  const malformedMatches: MalformedMatch[] = []
  for (const match of text.matchAll(MALFORMED_APPLY_RE)) {
    const captured = match[1]
    if (!captured) continue
    const captureStart = match.index! + match[0].indexOf(captured)
    const captureEnd = captureStart + captured.length
    const overlapsValid = validMatches.some(
      (v) => captureStart < v.end && captureEnd > v.start,
    )
    if (overlapsValid) continue
    malformedMatches.push({
      start: captureStart,
      end: captureEnd,
      raw: captured,
    })
  }

  // Merge ordered ranges and emit segments.
  type Range =
    | { kind: 'apply'; start: number; end: number; block: ApplyBlock }
    | { kind: 'malformed'; start: number; end: number; raw: string }
  const ranges: Range[] = [
    ...validMatches.map((v) => ({
      kind: 'apply' as const,
      start: v.start,
      end: v.end,
      block: v.block,
    })),
    ...malformedMatches.map((m) => ({
      kind: 'malformed' as const,
      start: m.start,
      end: m.end,
      raw: m.raw,
    })),
  ].sort((a, b) => a.start - b.start)

  const segments: ParsedContent['segments'] = []
  let lastIndex = 0

  for (const range of ranges) {
    if (range.start > lastIndex) {
      const textBefore = stripOrphanFences(
        text.slice(lastIndex, range.start).trim(),
      )
      if (textBefore) segments.push({ type: 'text', content: textBefore })
    }

    if (range.kind === 'apply') {
      segments.push({ type: 'apply', block: range.block })
    } else {
      segments.push({ type: 'malformed', raw: range.raw })
    }

    lastIndex = range.end
  }

  if (lastIndex < text.length) {
    const remaining = stripOrphanFences(text.slice(lastIndex).trim())
    if (remaining) segments.push({ type: 'text', content: remaining })
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content: text })
  }

  return { segments }
}

/**
 * Returns true if the text contains at least one apply-block marker
 * (including malformed openers that still emit `<<<END_APPLY>>>`).
 * Triggers ApplyableResponse so we can surface a "couldn't render" notice
 * instead of leaking raw markers as plain text.
 */
export function hasApplyBlocks(text: string): boolean {
  return /<<<APPLY\s/.test(text) || /<<<END_APPLY>>>/.test(text)
}

/**
 * Remove leftover ``` fence lines that surrounded an apply-block but
 * weren't consumed by the main regex (e.g. lone closing/opening fences
 * on adjacent lines between two apply blocks).
 */
function stripOrphanFences(text: string): string {
  return text
    .replace(/^[ \t]*```[a-zA-Z0-9_-]*[ \t]*\n?/gm, '')
    .replace(/\n?[ \t]*```[ \t]*$/gm, '')
    .replace(/^[ \t]*```[a-zA-Z0-9_-]*[ \t]*$/gm, '')
    .trim()
}

// ── Block Outline Extraction ──

/**
 * Extracts a flat outline of blocks from TipTap JSON for sending to the LLM.
 * Captures headings and the first ~80 chars of each paragraph to give the LLM
 * enough context to understand document structure.
 */
export function extractBlockOutline(tiptapJson: any): BlockOutlineEntry[] {
  const entries: BlockOutlineEntry[] = []
  const content = tiptapJson?.content ?? []

  for (const node of content) {
    const id = node.attrs?.id
    if (!id) continue

    const text = extractNodeText(node).slice(0, 120)
    if (!text) continue

    entries.push({
      id,
      type: node.type,
      ...(node.type === 'heading' && node.attrs?.level ? { level: node.attrs.level } : {}),
      text,
    })
  }

  return entries
}

/**
 * Formats block outline entries into a markdown string for the LLM context.
 * Example:
 *   DOCUMENT BLOCKS:
 *   - [heading#a1b2c3 level=2] "Purpose"
 *   - [paragraph#d4e5f6] "This policy establishes requirements for..."
 */
export function formatBlockOutline(entries: BlockOutlineEntry[]): string {
  if (entries.length === 0) return ''

  const lines = entries.map((e) => {
    const typeLabel = e.level ? `${e.type} level=${e.level}` : e.type
    return `- [${typeLabel}#${e.id}] "${e.text}"`
  })

  return `DOCUMENT BLOCKS:\n${lines.join('\n')}`
}

// ── Helpers ──

function extractNodeText(node: any): string {
  if (node.type === 'text') return node.text ?? ''
  if (!node.content || !Array.isArray(node.content)) return ''
  return node.content.map(extractNodeText).join('')
}
